import { IonHeader, IonIcon, IonModal, IonText } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { xMarkIcon } from "@/lib/icons/@heroicons/react/20/solid";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { useSelectorModalStore } from "@/lib/store/modal";

/** Prefix the API uses for missing custom-field keys: `customField:<definitionId>` (#383). */
const CUSTOM_FIELD_KEY_PREFIX = "customField:";

/**
 * Resolve a missing-field key (from `GET /me` profileCompleteness) to a human label.
 *
 * - Static keys (`photo`/`dayOfBirth`/`address`) map to fixed labels.
 * - `customField:<id>` keys resolve to the custom-field definition's real **name** via
 *   `customFieldNames` (#497). Previously every custom field fell through to a shared generic
 *   "Extra gegevens" label, so multiple missing custom fields looked like duplicates.
 *
 * Returns `null` (⇒ skip the row) while definitions haven't loaded yet (`customFieldNames`
 * undefined) so the nudge doesn't flash a generic placeholder; falls back to the generic label only
 * when definitions are loaded but the id is genuinely unknown.
 */
function resolveMissingFieldLabel(
  key: string,
  customFieldNames: Record<string, string> | undefined,
  t: (k: string, d: string) => string,
): string | null {
  switch (key) {
    case "photo":
      return t("modals:profile-nudge.fields.photo", "Profielfoto");
    case "dayOfBirth":
      return t("modals:profile-nudge.fields.dayOfBirth", "Geboortedatum");
    case "address":
      return t("modals:profile-nudge.fields.address", "Adres");
  }

  if (key.startsWith(CUSTOM_FIELD_KEY_PREFIX)) {
    if (customFieldNames === undefined) {
      // Definitions still loading — withhold the row rather than flash "Extra gegevens".
      return null;
    }
    const id = key.slice(CUSTOM_FIELD_KEY_PREFIX.length);
    return customFieldNames[id] ?? t("modals:profile-nudge.fields.customField", "Extra gegevens");
  }

  return t("modals:profile-nudge.fields.customField", "Extra gegevens");
}

/**
 * Build the ordered, de-duplicated list of labels to render for the missing fields. De-duping by
 * label collapses the "multiple identical Extra gegevens rows" the old code produced (#497) and
 * keeps each missing field to a single row. Exported for unit testing.
 */
export function buildMissingFieldLabels(
  missingFields: string[],
  customFieldNames: Record<string, string> | undefined,
  t: (k: string, d: string) => string,
): string[] {
  const labels: string[] = [];
  const seen = new Set<string>();

  for (const key of missingFields) {
    const label = resolveMissingFieldLabel(key, customFieldNames, t);
    if (label === null || seen.has(label)) {
      continue;
    }
    seen.add(label);
    labels.push(label);
  }

  return labels;
}

/**
 * Presentational body of the nudge. Extracted (and exported) so it can be rendered/asserted in
 * tests without IonModal's portal/template behaviour, which jsdom does not project into the
 * queryable DOM.
 */
export function ProfileNudgeBody({
  missingFields,
  customFieldNames,
  onComplete,
  onDismiss,
}: {
  missingFields: string[];
  /**
   * Map of custom-field definitionId → name, used to resolve `customField:<id>` keys to their real
   * names (#497). `undefined` while the definitions are still loading — custom-field rows are then
   * withheld to avoid flashing a generic placeholder.
   */
  customFieldNames?: Record<string, string>;
  onComplete: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  const labels = buildMissingFieldLabels(missingFields, customFieldNames, t);

  return (
    <div className="txt-primary flex h-full flex-col gap-4">
      <IonText className="text-3xl font-bold">
        {t("modals:profile-nudge.title", "Maak je profiel compleet")}
      </IonText>

      <IonText className="text-balance text-base text-txt-secondary">
        {t(
          "modals:profile-nudge.description",
          "Vul de ontbrekende gegevens aan zodat we je beter kunnen inplannen.",
        )}
      </IonText>

      {labels.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="profile-nudge-missing">
          {labels.map((label) => (
            <li
              key={label}
              className="rounded-lg bg-surface-secondary px-4 py-2 text-base text-txt-primary"
            >
              {label}
            </li>
          ))}
        </ul>
      )}

      <Button
        expand="block"
        className="m-0 mt-auto h-12 ion-rounded-2xl part-native:text-md part-native:font-bold"
        onClick={onComplete}
      >
        {t("modals:profile-nudge.actions.complete", "Profiel aanvullen")}
      </Button>
      <Button
        expand="block"
        slot="secondary"
        fill="outline"
        className="m-0 h-12 ion-rounded-2xl part-native:text-md part-native:font-bold"
        onClick={onDismiss}
      >
        {t("modals:profile-nudge.actions.later", "Later")}
      </Button>
    </div>
  );
}

/**
 * Profile-completion nudge. Reuses the shared modal-store + ModalProvider mechanism (same as
 * appExplainer / pushNotificationsDenied). Only ever opened by `useProfileNudge` for employees with
 * an incomplete profile after the interval has elapsed.
 */
export function ModalProfileNudge() {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const modal = useSelectorModalStore.use.profileNudge();
  const present = useSelectorModalStore.use.present();
  const dismiss = useSelectorModalStore.use.dismiss();

  const missingFields = modal.missingFields ?? [];

  // Resolve custom-field keys (`customField:<id>`) to their real definition names so the nudge
  // shows e.g. "Rijbewijs" instead of a generic "Extra gegevens" for every custom field (#497).
  // Only fetched while the nudge is actually open; the result is cached for subsequent opens.
  const { data: customFieldNames } = useQuery({
    queryKey: ["custom-field-definitions", "EMPLOYEE", "profile-nudge"],
    queryFn: () => extractionWrapper(api.customFieldDefinitions.getAll("EMPLOYEE", {})),
    enabled: modal.isOpen,
    select: (definitions) =>
      Object.fromEntries(definitions.map((definition) => [definition.id, definition.name])),
  });

  return (
    <IonModal
      ref={modalRef}
      isOpen={modal.isOpen}
      onWillPresent={() => present("profileNudge")}
      onWillDismiss={() => dismiss("profileNudge")}
    >
      <IonHeader className="ion-no-border">
        <Toolbar className="ion-py-2">
          <Button
            slot="secondary"
            className="touch-target min-h-0 ion-bg-transparent ion-rounded-full ion-p-2"
            onClick={() => modalRef.current?.dismiss()}
          >
            <IonIcon slot="icon-only" icon={xMarkIcon} className="size-5" />
          </Button>
        </Toolbar>
      </IonHeader>
      <Content inModal>
        <ProfileNudgeBody
          missingFields={missingFields}
          customFieldNames={customFieldNames}
          onComplete={() => {
            modalRef.current?.dismiss();
            history.push(Routes.PersonalInformation());
          }}
          onDismiss={() => modalRef.current?.dismiss()}
        />
      </Content>
    </IonModal>
  );
}
